<template>
	<section class="markets-most-popular lg:mb-8">
		<Subtitle
			weight="medium"
			class="mb-4 title-font"
		>
			Most Popular
		</Subtitle>
		<!-- Desktop View -->
		<div v-if="breakpoints.isGreaterOrEqual('lg') && news.length > 0">
			<NuxtLink :to="news[0].link">
				<img
					:src="`${
						hostname
					}cdn-cgi/image/quality=100,format=webp,width=700/${news[0].image}`"
					:alt="`${news[0].title} image`"
					class="w-full h-full object-cover rounded-xl bg-slate-300/[0.1]"
					loading="lazy"
					height="326"
					width="454"
				/>
			</NuxtLink>
		</div>
		<ol
			v-if="breakpoints.isGreaterOrEqual('lg') && news.length > 0"
			class="w-full"
		>
			<li
				v-for="(item, idx) in news"
				:key="idx"
				class="flex items-center gap-4 py-4 border-b border-slate-200"
			>
				<span
					class="flex items-center lg:min-w-9 min-h-9 justify-center rounded-full bg-[#4F8CFF] text-white font-bold text-lg"
				>
					{{ idx + 1 }}
				</span>
				<NuxtLink
					:to="item.slug"
					class="flex items-center min-w-9 min-h-9 justify-center rounded-full text-white font-bold text-lg bg-transparent"
				>
					<h3
						class="text-lg font-medium text-[#101828] hover:underline"
						v-html="item.title"
					></h3>
				</NuxtLink>
			</li>
		</ol>
		<!-- Mobile View -->
		<div
			v-else
			class="relative"
		>
			<div
				class="flex gap-4 overflow-x-auto snap-x snap-mandatory hide-scrollbar lg:pb-4 scroll-smooth"
				ref="scrollContainer"
			>
				<NuxtLink
					v-for="(item, idx) in news"
					:key="idx"
					:to="item.slug"
					:class="[
						'flex-shrink-0 w-[60vw] hover:underline lg:w-[80vw] bg-[#F9FAFB] rounded-xl p-4 lg:p-6 snap-center transition-all duration-300',
						activeIndex === idx ? '' : 'scale-100'
					]"
				>
					<div
						:class="[
							'flex items-center justify-center lg:w-12 lg:h-12 w-9 h-9 rounded-full text-white font-bold text-xl mb-4 transition-all duration-300',
							activeIndex === idx
								? 'bg-[#4F8CFF] scale-110'
								: 'bg-[#4F8CFF] bg-opacity-70'
						]"
					>
						{{ idx + 1 }}
					</div>
					<p
						class="text-lg font-medium text-[#101828] hover:underline"
						v-html="item.title"
					></p>
				</NuxtLink>
			</div>

			<!-- Navigation Arrows - Mobile Only -->
			<div class="flex gap-3 mt-4 lg:hidden">
				<button
					@click="goToPrevious"
					:disabled="activeIndex === 0"
					:class="[
						'flex items-center justify-center text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed',
						activeIndex === 0
							? 'opacity-50 cursor-not-allowed'
							: 'hover:scale-105 active:scale-95'
					]"
					style="
						box-sizing: border-box;
						padding: 9px;
						gap: 4.5px;
						width: 36px;
						height: 36px;
						background: linear-gradient(90deg, #50b5ff 0%, #be55ff 99.95%);
						border: 0.5625px solid #ffffff;
						border-radius: 4px;
						flex: none;
						order: 0;
						flex-grow: 0;
					"
				>
					<svg
						width="18"
						height="18"
						viewBox="0 0 18 18"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M11.25 13.5L6.75 9L11.25 4.5"
							stroke="currentColor"
							stroke-width="1.125"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
				</button>
				<button
					@click="goToNext"
					:disabled="activeIndex === items.length - 1"
					:class="[
						'flex items-center justify-center text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed',
						activeIndex === items.length - 1
							? 'opacity-50 cursor-not-allowed'
							: 'hover:scale-105 active:scale-95'
					]"
					style="
						box-sizing: border-box;
						padding: 9px;
						gap: 4.5px;
						width: 36px;
						height: 36px;
						background: linear-gradient(90deg, #50b5ff 0%, #be55ff 99.95%);
						border: 0.5625px solid #ffffff;
						border-radius: 4px;
						flex: none;
						order: 0;
						flex-grow: 0;
					"
				>
					<svg
						width="18"
						height="18"
						viewBox="0 0 18 18"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M6.75 13.5L11.25 9L6.75 4.5"
							stroke="currentColor"
							stroke-width="1.125"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
				</button>
			</div>
		</div>
	</section>
</template>
<script setup>
import { breakpointsTailwind } from "@vueuse/core";
import { ref, onMounted, onUnmounted } from "vue";

const breakpoints = useBreakpoints(breakpointsTailwind);
const scrollContainer = ref(null);
const activeIndex = ref(0);

const items = [
	"AGNC Investment Corp: Unexpected Financial Twist?",
	"Pentair's Stock Evaluation: What Lies Ahead?",
	"Vale S.A. Stock Upgrade: A Buy Signal?",
	"BigBear.ai Stock Surge: Road to Sustained Growth?",
	"IVZ Price Fluctuations: Market Analyses",
	"iCoreConnect Inc.: Stock Surge or Bubble Burst?"
];

const {
	public: { hostname }
} = useRuntimeConfig();

defineProps({
	news: {
		type: Array,
		required: true
	}
});

// Navigation functions
const goToNext = () => {
	if (activeIndex.value < items.length - 1) {
		activeIndex.value++;
		scrollToActiveItem();
	}
};

const goToPrevious = () => {
	if (activeIndex.value > 0) {
		activeIndex.value--;
		scrollToActiveItem();
	}
};

const scrollToActiveItem = () => {
	if (scrollContainer.value) {
		const itemWidth = scrollContainer.value.scrollWidth / items.length;
		const scrollPosition = activeIndex.value * itemWidth;
		scrollContainer.value.scrollTo({
			left: scrollPosition,
			behavior: "smooth"
		});
	}
};

// Auto-detect active item based on scroll position
const handleScroll = () => {
	if (scrollContainer.value) {
		const scrollLeft = scrollContainer.value.scrollLeft;
		const itemWidth = scrollContainer.value.scrollWidth / items.length;
		const newActiveIndex = Math.round(scrollLeft / itemWidth);

		if (
			newActiveIndex !== activeIndex.value &&
			newActiveIndex >= 0 &&
			newActiveIndex < items.length
		) {
			activeIndex.value = newActiveIndex;
		}
	}
};

onMounted(() => {
	if (scrollContainer.value) {
		scrollContainer.value.addEventListener("scroll", handleScroll);
	}
});

onUnmounted(() => {
	if (scrollContainer.value) {
		scrollContainer.value.removeEventListener("scroll", handleScroll);
	}
});
</script>
<style scoped>
.hide-scrollbar {
	-ms-overflow-style: none; /* IE and Edge */
	scrollbar-width: none; /* Firefox */
}
.hide-scrollbar::-webkit-scrollbar {
	display: none; /* Chrome, Safari, and Opera */
}

/* Smooth scrolling for the carousel */
.scroll-smooth {
	scroll-behavior: smooth;
}

/* Enhanced transitions for the slideshow */
.transition-all {
	transition-property: all;
	transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Active item highlighting */
.ring-2 {
	box-shadow: 0 0 0 2px rgba(79, 140, 255, 0.5);
}

/* Navigation button states */
button:disabled {
	cursor: not-allowed;
	opacity: 0.5;
}

button:not(:disabled):hover {
	transform: translateY(-1px);
}

button:not(:disabled):active {
	transform: scale(0.95);
}

/* Mobile-specific improvements */
@media (max-width: 1023px) {
	.snap-center {
		scroll-snap-align: center;
	}

	.snap-x {
		scroll-snap-type: x mandatory;
	}
}
</style>
